import { PencilRuler, UsersRound } from "lucide-react";
import { lazy, Suspense, useState, type ReactNode } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
  useLocation,
  type DataRouter,
} from "react-router";
import { atLeast, COURSE_SITE_ROLES, managerRole, type Role } from "../schema/core.ts";
import { useSession } from "./auth.ts";
import type { SiteCopy } from "./i18n/site.ts";
import { useSiteCopy } from "./i18n/useSiteCopy.ts";
import { SiteLayout } from "./layout/SiteLayout.tsx";
import { HomePage } from "./pages/HomePage.tsx";
import { CourseOverviewPage } from "./pages/CourseOverview.tsx";
import { LessonPage } from "./pages/LessonPage.tsx";
import { LoginPage } from "./pages/LoginPage.tsx";
import { NoAccessPage, NotFoundPage } from "./pages/NotFoundPage.tsx";
import { RouteErrorPage } from "./pages/RouteErrorPage.tsx";
import { CourseKitProvider, type CourseSiteBrand } from "./provider.tsx";
import { loginPath } from "./routing.ts";
import { PageLoading } from "./ui/Spinner.tsx";
import { Toaster } from "./ui/toast.tsx";

// Learners never reach /maak: keep the builder (dnd-kit, editors) out of the main bundle.
const CourseBuilder = lazy(() =>
  import("./builder/index.ts").then((module) => ({ default: module.CourseBuilder })),
);

// The user manager only; its copy catalog travels in the same chunk.
const CourseRolesPage = lazy(() =>
  import("./roles/CourseRolesPage.tsx").then((module) => ({ default: module.CourseRolesPage })),
);

const BUILDER_BASE = "/maak";
const USERS_PATH = `${BUILDER_BASE}/gebruikers`;
/** The same role the users API requires, so the UI gate and the API gate cannot drift apart. */
const USER_MANAGER = managerRole(COURSE_SITE_ROLES);

/** The kit layout plus the builder (admins) and users (user manager) account-menu entries. */
const CourseLayout = (): ReactNode => {
  const copy = useSiteCopy().account;
  return (
    <SiteLayout
      accountLinks={[
        { to: BUILDER_BASE, label: copy.builder, icon: PencilRuler, access: "admin" },
        { to: USERS_PATH, label: copy.users, icon: UsersRound, access: USER_MANAGER },
      ]}
    />
  );
};

interface RoleGateProps {
  minimum: Role;
  /** Which `noAccess` text explains the page to someone below `minimum`. */
  denied: Exclude<keyof SiteCopy["noAccess"], "title" | "back">;
  children: ReactNode;
}

/** A lazy page for `minimum` and above; others sign in first or see why they cannot. */
const RoleGate = ({ minimum, denied, children }: RoleGateProps): ReactNode => {
  const copy = useSiteCopy();
  const { user, loading } = useSession();
  const { pathname, search } = useLocation();
  if (loading) return <PageLoading label={copy.common.loading} />;
  if (!user) return <Navigate to={loginPath(`${pathname}${search}`)} replace />;
  if (!atLeast(user.role, minimum)) return <NoAccessPage body={copy.noAccess[denied]} />;
  return <Suspense fallback={<PageLoading label={copy.common.loading} />}>{children}</Suspense>;
};

const SiteToaster = (): ReactNode => {
  const { common } = useSiteCopy();
  return <Toaster label={common.notifications} dismissLabel={common.dismiss} />;
};

/**
 * A data router (not <BrowserRouter>): SiteLayout's ScrollRestoration needs one. The
 * pathless route keeps header and footer around a page that fails; the root error
 * element only shows when the layout itself fails.
 */
const createRouter = (): DataRouter =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route element={<CourseLayout />} errorElement={<RouteErrorPage standalone />}>
        <Route errorElement={<RouteErrorPage />}>
          <Route index element={<HomePage />} />
          <Route path="cursus/:courseSlug" element={<CourseOverviewPage />} />
          <Route path="cursus/:courseSlug/:lessonSlug" element={<LessonPage />} />
          <Route path="inloggen" element={<LoginPage />} />
          {/* Ranked above the `maak/*` splat by react-router. */}
          <Route
            path={USERS_PATH.slice(1)}
            element={
              <RoleGate minimum={USER_MANAGER} denied="roles">
                <CourseRolesPage />
              </RoleGate>
            }
          />
          <Route
            path={`${BUILDER_BASE.slice(1)}/*`}
            element={
              <RoleGate minimum="admin" denied="builder">
                <CourseBuilder basePath={BUILDER_BASE} />
              </RoleGate>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>,
    ),
  );

interface CourseSiteProps {
  brand: CourseSiteBrand;
  /** Origin of the course Worker; omit when the SPA and API share an origin. */
  apiBaseUrl?: string;
}

/** The whole course site: learner pages, login, the course builder and the owners' role page. */
export const CourseSite = ({ brand, apiBaseUrl }: CourseSiteProps): ReactNode => {
  const [router] = useState(createRouter);
  return (
    <CourseKitProvider brand={brand} apiBaseUrl={apiBaseUrl}>
      <RouterProvider router={router} />
      <SiteToaster />
    </CourseKitProvider>
  );
};
